import AlarmAddIcon from '@mui/icons-material/AlarmAdd';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DownloadIcon from '@mui/icons-material/Download';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SendIcon from '@mui/icons-material/Send';
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    Box,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    GridCellParams,
    GridColDef,
    GridComparatorFn,
    GridCsvGetRowsToExportParams,
    GridFilterItem,
    GridRenderCellParams,
    GridValueFormatterParams,
    ValueOptions,
    getGridSingleSelectOperators,
    gridExpandedSortedRowIdsSelector,
    useGridApiRef,
} from '@mui/x-data-grid-premium';
import { GridSingleSelectColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import dayjs from 'dayjs';
import capitalize from 'lodash.capitalize';
import { usePopupState } from 'material-ui-popup-state/hooks';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { CustomerUserDetails, DeviceV3, ReportInstance } from '../../../backendsdk';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { TabPanel, a11yProps } from '../../../components/Tab';
import {
    TrackedButton as Button,
    TrackedIconButton as IconButton,
    TrackedMenuItem as MenuItem,
    TrackedDataGrid as StyledDataGrid,
    TrackedTab as Tab,
} from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import { ExtendedFleetDriver, getDriverDisplayName } from '../../../hooks/drivers';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import { getDateColumnType, getDateTimeColumnType, localeObjectMap } from '../../../utils/DataGridDateTime';
import { reportsPageDefs } from '../../../utils/Pages';
import { getStorageItem } from '../../../utils/Storage';
import { formatLicensePlateWithInternalId, uniq } from '../../../utils/Str';
import {
    buildNormalizedPlateToInternalIdMap,
    getInternalIdForNormalizedPlate,
} from '../../../utils/deviceInternalIdLookup';
import { MILES_TO_KM_FACTOR } from '../../../utils/location';
import palette from '../../ColorPalette';
import AccidentDetailsComponent from '../Accident/AccidentDetails';
import { AccidentPageVersion } from '../Accident/Defs';
import { BEHAVIOR_MAP } from '../Coaching/SessionDetails';
import { WELLNESS_TYPE_MAP } from '../Device/WellnessBox';
import { COMBINED_EVENT_TYPE_MAP } from '../Event/Defs';
import EventDetailsComponent from '../Event/EventDetails';
import { gridLocalization } from '../OTA/MuiDeviceTable';
import PastReports from './PastReports';
import { EMPTY_REPORT, REPORT_TYPE_TO_URL_PARAM } from './ReportComponent';
import ReportAlertDetailsComponent, {
    DEVICE_FILTER_OPTIONS,
    FILTER_AVAILABLE,
    REPORT_TYPE_OPTIONS,
    SCOPE_OPTIONS,
} from './ReportDetails';
import {
    AccidentReportForm,
    AlertReport,
    CoachingSessionsStatusForm,
    DeviceWellnessForm,
    GeofenceEventForm,
    MalfunctionsReportForm,
    Report,
    SESSION_STATUS_FILTER_KEY,
    SESSION_STATUS_VALUES,
    SafetyEventForm,
} from './ReportOptions';
import ReportWellnessCell from './ReportWellnessCell';
import ScheduledReportsComponent from './ScheduledReports';
import SendReportModal from './SendReportModal';

export const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
export const checkedIcon = <CheckBoxIcon fontSize="small" />;

/** S3 `report_type` in historical JSON may still be legacy pending/completed. */
const LEGACY_COACHING_SESSION_S3_TYPES = ['PENDING_SESSIONS', 'COMPLETED_SESSIONS'] as const;
const COACHING_SESSION_GRID_REPORT_TYPES: readonly string[] = [
    ...LEGACY_COACHING_SESSION_S3_TYPES,
    REPORT_TYPE_OPTIONS.COACHING_SESSIONS,
];

const behaviorsSortComparator: GridComparatorFn<string[]> = (behaviors1: string[], behaviors2: string[]) => {
    return behaviors1.length - behaviors2.length;
};

const behaviorsFilterOperators = getGridSingleSelectOperators()
    .filter((operator) => operator.value === 'isAnyOf')
    .map((operator) => {
        const newOperator = { ...operator };
        const newGetApplyFilterFn = (filterItem: GridFilterItem) => {
            return (params: GridCellParams): boolean => {
                const value = params.value as string[];
                let isOk = true;
                filterItem?.value?.forEach((item: string) => {
                    isOk = isOk && value.includes(item);
                });
                return isOk;
            };
        };
        newOperator.getApplyFilterFn = newGetApplyFilterFn;
        return newOperator;
    });

interface TableColumn {
    field: string | number;
    type: string;
}

type TableRow = Record<string, string | number>;

interface ReportData {
    report_type: keyof typeof REPORT_TYPE_OPTIONS;
    scope: keyof typeof SCOPE_OPTIONS;
    filters: object;
    table_columns: TableColumn[];
    table_rows: TableRow[];
}

interface ReportContentProps {
    reportType: string;
    setSelectedReportType: (reportType: string | null) => void;
    devices: DeviceV3[];
    drivers: ExtendedFleetDriver[];
    driverSubFleets: string[];
    users: CustomerUserDetails[];
    geofences: Record<string, string>;
    setAlert: CallableFunction;
    scheduledReports: AlertReport[];
    isLoadingScheduled: boolean;
    handlingStatusList?: Array<string>;
    setHandlingStatusList?: CallableFunction;
    initialInstance?: ReportInstance;
}

const ReportContent: React.FC<ReportContentProps> = (props: ReportContentProps) => {
    const empty_report = JSON.parse(
        JSON.stringify({
            ...EMPTY_REPORT,
            report_type: props.reportType,
            filters:
                props.reportType === REPORT_TYPE_OPTIONS.COACHING_SESSIONS
                    ? { [SESSION_STATUS_FILTER_KEY]: [...SESSION_STATUS_VALUES] }
                    : {},
        }),
    );
    const [details, setDetails] = useState<Report>(empty_report);
    const { reportId, instanceId } = useParams<{ reportId: string; instanceId: string }>();
    const [tab, setTab] = useState<number>(!!reportId ? 1 : 0);
    const [detailsChanged, setDetailsChanged] = useState<boolean>(true);
    const [reportInstance, setReportInstance] = useState<ReportInstance | undefined>(props.initialInstance);
    const [reportData, setReportData] = useState<ReportData>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [selectedEventId, setSelectedEventId] = useState<string>();
    const [selectedAccidentId, setSelectedAccidentId] = useState<string>();
    const [instanceStatusAttempts, setInstanceStatusAttempts] = useState<number>(0);
    const [isSendEmailOpen, setIsSendEmailOpen] = useState<boolean>(false);
    const [isLoadingUpdate, setIsLoadingUpdate] = useState<boolean>(false);
    const [isScheduleOpen, setIsScheduleOpen] = useState<boolean>(false);
    const [isSendOpen, setIsSendOpen] = useState<boolean>(false);
    const [pastReports, setPastReports] = useState<ReportInstance[]>([]);
    const [isLoadingPastReports, setIsLoadingPastReports] = useState<boolean>(true);
    const timerRef = useRef<NodeJS.Timeout>();
    const { i18n, t } = useTranslation();
    const { api } = useApi();
    const { profile } = useProfile();
    const isMobile = useIsMobile();
    const history = useHistory();
    const apiRef = useGridApiRef();

    const locale = localeObjectMap[profile.locale];

    const ALLOW_VIEW_ALL_SUB_FLEETS = [...reportsPageDefs.permissions.L1];

    useEffect(() => {
        setIsLoadingPastReports(true);
        api.apiV0ReportRunGet({ reportType: props.reportType })
            .then((res) => {
                setPastReports(res.data);
            })
            .finally(() => setIsLoadingPastReports(false));
    }, []);

    useEffect(() => {
        if (!!instanceId) {
            setReportInstance(props.initialInstance);
        }
    }, [props.initialInstance]);

    const usersById = useMemo(() => {
        return props.users.reduce((acc, user) => {
            acc[user.user_id] = user;
            return acc;
        }, {} as Record<string, CustomerUserDetails>);
    }, [props.users]);

    const showVehicleInternalId = profile.customer.settings.show_vehicle_internal_id ?? false;
    const plateToInternalId = useMemo(
        () => buildNormalizedPlateToInternalIdMap(props.devices.map((d) => d.device)),
        [props.devices],
    );

    const translateFunctions: Record<string, CallableFunction> = {
        event_type: (value?: string) =>
            !!value && !!COMBINED_EVENT_TYPE_MAP[value] ? t(COMBINED_EVENT_TYPE_MAP[value]) : capitalize(value),
        stage: (value?: string) =>
            !!value
                ? t(`content.accidents.stage.${profile.customer.settings.country}.${value}`, capitalize(value))
                : value,
        category: (value?: string) =>
            !!value
                ? t(
                      `content.accidents.form.categories.${value.toLocaleLowerCase().replaceAll(' ', '_')}`,
                      capitalize(value),
                  )
                : value,
        damage_type: (value?: string) =>
            !!value ? t(`content.accidents.damage.${value.toLocaleLowerCase()}`, capitalize(value)) : value,
        at_fault: (value?: string) =>
            !!value ? t(`content.accidents.form.at_fault.${value}`, capitalize(value)) : value,
        in_cabin_wellness: (value?: string) =>
            !!value
                ? t(WELLNESS_TYPE_MAP[value.toLocaleLowerCase() as keyof typeof WELLNESS_TYPE_MAP], capitalize(value))
                : value,
        front_facing_wellness: (value?: string) =>
            !!value
                ? t(WELLNESS_TYPE_MAP[value.toLocaleLowerCase() as keyof typeof WELLNESS_TYPE_MAP], capitalize(value))
                : value,
        malfunction: (value?: string) =>
            !!value
                ? t(
                      `content.malfunctions.table.type.${value.toLocaleLowerCase()}`,
                      capitalize(value.toLocaleLowerCase().replaceAll('_', ' ')),
                  )
                : value,
        behaviors: (value?: string) => (!!value ? t(BEHAVIOR_MAP[value]) : value),
        session_status: (value?: string) =>
            value
                ? t(`content.reports.form.filter.session_status_${(value as string).toLocaleLowerCase()}`, value)
                : '',
        method: (value?: string) =>
            !!value ? t(`content.reports.table.completion_method_${(value as string).toLocaleLowerCase()}`, value) : '',
    };

    const getInstanceStatus = (instanceId: number) => {
        setInstanceStatusAttempts((prev) => prev + 1);
        api.apiV0ReportRunInstanceIdGet({ instanceId })
            .then((res) => {
                setReportInstance(res.data);
            })
            .catch(() => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
                setIsError(true);
                setIsLoading(false);
                setIsSendEmailOpen(false);
                setInstanceStatusAttempts(0);
            });
    };

    const loadReportData = (download_url: string) => {
        axios
            .get(download_url)
            .then((res) => {
                setReportData(res.data);
                setDetailsChanged(false);
            })
            .catch(() => {
                setIsError(true);
            })
            .finally(() => {
                setIsLoading(false);
                setIsSendEmailOpen(false);
                setInstanceStatusAttempts(0);
            });
    };

    useEffect(() => {
        if (!!reportInstance) {
            if (!reportInstance.download_url) {
                timerRef.current = setTimeout(() => {
                    getInstanceStatus(reportInstance.pk);
                }, 3000);
                return () => {
                    if (timerRef.current) {
                        clearTimeout(timerRef.current);
                    }
                };
            } else {
                loadReportData(reportInstance.download_url);
            }
        }
    }, [reportInstance, api]);

    useEffect(() => {
        if (instanceStatusAttempts === 2 || instanceStatusAttempts === 5) {
            setIsSendEmailOpen(true);
        }
    }, [instanceStatusAttempts]);

    const keys = details.filters == undefined ? [] : Object.keys(details.filters);
    const deviceFilterKey =
        Object.keys(DEVICE_FILTER_OPTIONS).find((key) => keys.includes(key)) ?? DEVICE_FILTER_OPTIONS.ALL;

    const filteredDeviceFilterOptions = DEVICE_FILTER_OPTIONS;
    if (!ALLOW_VIEW_ALL_SUB_FLEETS.includes(profile.role) && profile.sub_fleets.length === 1) {
        delete filteredDeviceFilterOptions.DEPARTMENT;
    }

    const deviceFilterOptions = useMemo(() => {
        const options: Record<string, string[]> = { [DEVICE_FILTER_OPTIONS.ALL]: ['ALL'] };
        const filteredDevices =
            ALLOW_VIEW_ALL_SUB_FLEETS.includes(profile.role) || profile.sub_fleets.length === 0
                ? props.devices
                : props.devices.filter((d) => profile.sub_fleets.includes(d.device.customer_sub_fleet));
        options[DEVICE_FILTER_OPTIONS.LICENSE_PLATE] = filteredDevices
            .map((d) => d.device.license_plate)
            .filter(uniq)
            .sort((a, b) => a.localeCompare(b));
        options[DEVICE_FILTER_OPTIONS.DEPARTMENT] = ['SAFETY_EVENTS', 'GEOFENCE_EVENTS'].includes(props.reportType)
            ? props.driverSubFleets
            : props.devices
                  .map((d) => d.device.customer_sub_fleet ?? '')
                  .filter(uniq)
                  .sort((a, b) => a.localeCompare(b));
        const showDriverCode = profile.customer.settings.show_driver_code ?? false;
        options[DEVICE_FILTER_OPTIONS.DRIVER] = props.drivers
            .filter((d) => !d.is_auto_generated && d.active)
            .map((d) => getDriverDisplayName(d, showDriverCode))
            .filter(uniq)
            .sort((a, b) => a.localeCompare(b));

        return options;
    }, [deviceFilterKey, profile, props.devices, props.drivers, props.reportType]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = (target: string, value: any) => {
        setDetailsChanged(true);
        setDetails((prev) => {
            const newDetails = Object.fromEntries(
                Object.entries(prev).map(([key, origin_value]) => [key, key === target ? value : origin_value]),
            );
            newDetails[target] = value;
            return newDetails as Report;
        });
    };

    const handleGenerate = (reportDetails: Report) => {
        setIsLoading(true);
        api.apiV0ReportRunPost({
            reportInstanceRequest: {
                report_type: reportDetails.report_type,
                report_scope: reportDetails.report_scope,
                filters: reportDetails.filters,
            },
        })
            .then((res) => {
                setReportInstance(res.data);
                history.push(`/reports/${REPORT_TYPE_TO_URL_PARAM[props.reportType]}/run/${res.data.pk}`);
            })
            .catch(() => {
                setIsError(true);
                setIsLoading(false);
            });
    };

    const handleGenerateScheduled = (values: Report) => {
        setTab(0);
        setDetails(values);
        handleGenerate(values);
    };

    let columns: GridColDef[] = [];
    let rows: TableRow[] = [];
    if (reportData) {
        rows = reportData.table_rows.map((row: TableRow, idx: number) => {
            const updatedRow = { ...row };
            if ('distance' in row) {
                if (profile.locale === 'us') {
                    updatedRow['distance'] = (row['distance'] as number) / MILES_TO_KM_FACTOR;
                }
            } else if ('sent_by' in row) {
                updatedRow['sent_by'] = usersById[row['sent_by'] as string | number]?.name || '';
            } else if ('assignee' in row) {
                updatedRow['assignee'] = usersById[row['assignee']]?.name || '';
            }
            return {
                id: idx,
                ...updatedRow,
            };
        });
        columns = reportData.table_columns.map((column) => {
            let updatedColumn: GridColDef = {
                ...column,
                headerName:
                    typeof column.field === 'number'
                        ? dayjs.unix(column.field).format(profile.dateFormat)
                        : t(
                              `content.reports.table.${column.field}${
                                  column.field === 'distance' ? `_${profile.locale}` : ''
                              }`,
                          ),
                field: column.field.toString(),
            };
            if (column.field === 'license_plate') {
                updatedColumn = {
                    ...updatedColumn,
                    renderCell: (params: GridRenderCellParams) => {
                        const plate = String(params.value ?? '');
                        const internalId = getInternalIdForNormalizedPlate(plateToInternalId, plate);
                        const display = formatLicensePlateWithInternalId(plate, internalId, showVehicleInternalId);
                        return <Typography variant="body2">{display}</Typography>;
                    },
                };
            }
            if (reportData.report_type === REPORT_TYPE_OPTIONS.DRIVER_SCORES && typeof column.field === 'number') {
                updatedColumn = {
                    ...updatedColumn,
                    renderCell: (params: GridRenderCellParams) => {
                        let value = params.value;
                        const field = params.field;
                        if (value === null) {
                            if (params.row[`${field}_reason`]) {
                                value = t(`content.safety.score_${params.row[`${field}_reason`]}`);
                            }
                        }
                        return <Typography variant="body2">{value}</Typography>;
                    },
                };
            }
            if (column.type === 'datetime') {
                updatedColumn = {
                    ...updatedColumn,
                    ...getDateTimeColumnType(locale),
                    valueFormatter: (params: GridValueFormatterParams) =>
                        !!params.value ? dayjs.unix(params.value).format(profile.dateTimeFormat) : '',
                };
            } else if (column.type === 'date') {
                updatedColumn = {
                    ...updatedColumn,
                    ...getDateColumnType(locale),
                    valueFormatter: (params: GridValueFormatterParams) =>
                        !!params.value ? dayjs.unix(params.value).format(profile.dateFormat) : '',
                };
            } else if (column.type === 'singleSelect') {
                let values = Array.from(new Set(rows.map((row) => row[column.field]))).filter(Boolean);
                if (column.field === 'behaviors') {
                    values = Array.from(new Set(values.map((value) => value.toString().split(',')).flat())).filter(
                        Boolean,
                    );
                    updatedColumn = {
                        ...updatedColumn,
                        sortComparator: behaviorsSortComparator,
                        filterOperators: behaviorsFilterOperators,
                        renderCell: (params: GridRenderCellParams) => {
                            const value = params.value as string;
                            return (
                                <Typography variant="body2">
                                    {value
                                        .split(',')
                                        .map((v) => translateFunctions[column.field](v))
                                        .join(', ')}
                                </Typography>
                            );
                        },
                    };
                }
                let valueOptions: ValueOptions[] = values;
                if (column.field in translateFunctions) {
                    valueOptions = values.map((value) => {
                        return {
                            value,
                            label: translateFunctions[column.field](value),
                        };
                    });
                }
                updatedColumn = {
                    ...updatedColumn,
                    valueOptions,
                } as GridSingleSelectColDef;
            }
            if (['in_cabin_wellness', 'front_facing_wellness'].includes(column.field as string)) {
                const isInCabinWellness = column.field === 'in_cabin_wellness';
                updatedColumn = {
                    ...updatedColumn,
                    renderCell: (params: GridRenderCellParams) => {
                        const wellnessType = isInCabinWellness ? params.row.in_cabin_wellness_type : 'front_facing';
                        const wellnessId = isInCabinWellness
                            ? params.row.in_cabin_wellness_id
                            : params.row.front_facing_wellness_id;
                        const value = params.value;
                        const popupState = usePopupState({
                            variant: 'popover',
                            popupId: 'wellness-popover',
                        });
                        return (
                            <ReportWellnessCell
                                popupState={popupState}
                                title={translateFunctions[column.field](value as string)}
                                wellnessType={wellnessType}
                                wellnessId={wellnessId}
                                isInCabinWellness={isInCabinWellness}
                            />
                        );
                    },
                };
            }
            return {
                ...updatedColumn,
                flex: reportData.report_type === REPORT_TYPE_OPTIONS.ACCIDENTS_REPORT ? undefined : 1,
                renderHeader: (params) => (
                    <Typography variant="overline" sx={{ fontWeight: 500 }}>
                        {params.colDef.headerName}
                    </Typography>
                ),
            };
        }) as GridColDef[];
        if (
            reportData.report_type === 'PENDING_SESSIONS' ||
            reportData.report_type === REPORT_TYPE_OPTIONS.COACHING_SESSIONS
        ) {
            columns.push({
                field: 'send_reminder',
                type: 'string',
                headerName: t('content.reports.table.send_reminder'),
                renderCell: (params: GridRenderCellParams) => {
                    const [isSending, setIsSending] = useState<boolean>(false);
                    const handleSendReminder = (sessionId: number) => {
                        setIsSending(true);
                        api.apiV1CoachSessionIdRemindPost({ sessionId })
                            .then(() => {
                                props.setAlert({
                                    type: 'success',
                                    message: t('content.coaching.reminder_sent'),
                                    duration: 6000,
                                });
                            })
                            .catch(() => {
                                props.setAlert({
                                    type: 'error',
                                    message: t('content.coaching.reminder_failed'),
                                    duration: 6000,
                                });
                            })
                            .finally(() => setIsSending(false));
                    };

                    if (
                        reportData.report_type === REPORT_TYPE_OPTIONS.COACHING_SESSIONS &&
                        params.row.session_status &&
                        params.row.session_status !== 'PENDING'
                    ) {
                        return null;
                    }

                    return (
                        <IconButton
                            id={`btn-remind-${params.row.session_id}`}
                            disabled={isSending}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSendReminder(params.row.session_id as number);
                            }}
                        >
                            <SendIcon />
                        </IconButton>
                    );
                },
            });
        }
    }

    const hasResults = reportData !== undefined;

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                maxWidth: '1200px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            <ConfirmDialog
                isDialogOpen={isSendEmailOpen}
                isLoading={isLoadingUpdate}
                headerText="content.reports.get_by_email_header"
                bodyText={['content.reports.get_by_email_body1', 'content.reports.get_by_email_body2']}
                buttonText="content.reports.get_by_email"
                cancelText="content.reports.keep_waiting"
                buttonColor="secondary"
                onClose={() => setIsSendEmailOpen(false)}
                onConfirm={() => {
                    if (reportInstance) {
                        setIsLoadingUpdate(true);
                        api.apiV0ReportRunInstanceIdPatch({
                            instanceId: reportInstance.pk,
                            reportInstanceUpdate: { send_by_email: true },
                        }).then(() => {
                            setIsSendEmailOpen(false);
                            history.push('/reports');
                            props.setSelectedReportType(null);
                        });
                    }
                }}
            />
            {!!selectedEventId ? (
                <EventDetailsComponent
                    eventId={selectedEventId}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    isVisible={!!selectedEventId}
                    setIsVisible={() => setSelectedEventId('')}
                    onEventsPage={false}
                    setSelectedEventId={setSelectedEventId}
                    showEditVideo
                    showFindVideo
                    setAlert={props.setAlert}
                />
            ) : null}
            {!!selectedAccidentId && !!props.handlingStatusList && !!props.setHandlingStatusList ? (
                <AccidentDetailsComponent
                    pageVersion={(getStorageItem('OE-accident-page-version') as AccidentPageVersion) || 'v0'}
                    accidentId={selectedAccidentId}
                    refreshTable={() => null}
                    setIsLoadingModal={setIsLoading}
                    handlingStatusList={props.handlingStatusList}
                    setHandlingStatusList={props.setHandlingStatusList}
                    onClose={() => setSelectedAccidentId(undefined)}
                />
            ) : null}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    minHeight: 0,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: 0,
                        flexShrink: 0,
                    }}
                >
                    <IconButton
                        id="btn-back"
                        size="small"
                        onClick={() => {
                            history.push('/reports');
                            props.setSelectedReportType(null);
                        }}
                        sx={{ mr: 0.5 }}
                    >
                        <ArrowBackIosNewIcon />
                    </IconButton>
                    <Typography variant="h5">
                        {t(`content.reports.form.type.${props.reportType.toLocaleLowerCase()}`)}
                    </Typography>
                </Box>
                <Tabs
                    value={tab}
                    onChange={(_event, newValue) => {
                        history.push(`/reports/${REPORT_TYPE_TO_URL_PARAM[props.reportType]}`);
                        setTab(newValue);
                    }}
                    TabIndicatorProps={{ sx: { bgcolor: 'secondary.main', height: 3 } }}
                    sx={{ minHeight: 0, flexShrink: 0 }}
                    variant={isMobile ? 'fullWidth' : undefined}
                >
                    <Tab
                        label={<Typography fontSize={14}>{t('content.reports.generate_tab')}</Typography>}
                        {...a11yProps(0)}
                        id="generate-tab"
                        data-testid={`tab-0`}
                    />
                    <Tab
                        label={<Typography fontSize={14}>{t('content.reports.scheduled_tab')}</Typography>}
                        {...a11yProps(1)}
                        id="scheduled-tab"
                        data-testid={`tab-1`}
                    />
                    <Tab
                        label={<Typography fontSize={14}>{t('content.reports.past_reports')}</Typography>}
                        {...a11yProps(2)}
                        id="past-tab"
                        data-testid={`tab-2`}
                    />
                </Tabs>
                <TabPanel
                    value={tab}
                    index={0}
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        pt: 1.5,
                    }}
                >
                    {isScheduleOpen ? (
                        <ReportAlertDetailsComponent
                            type="REPORTS"
                            details={details}
                            devices={props.devices}
                            geofences={props.geofences}
                            onClose={() => setIsScheduleOpen(false)}
                            goToScheduledTab={() => setTab(1)}
                        />
                    ) : null}
                    {isSendOpen && !!reportInstance ? (
                        <SendReportModal
                            instanceId={reportInstance.pk}
                            onClose={() => setIsSendOpen(false)}
                            users={props.users}
                            setAlert={props.setAlert}
                        />
                    ) : null}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControl sx={{ width: '200px', minHeight: 0, flexShrink: 0, mr: 1 }}>
                            <InputLabel id="scope-dropdown-label">{t('content.reports.form.scope.header')}</InputLabel>
                            <Select
                                id="scope-dropdown"
                                data-testid="scope-dropdown"
                                labelId="scope-dropdown-label"
                                fullWidth
                                size="small"
                                label={t('content.reports.form.scope.header')}
                                value={details.report_scope}
                                onChange={(e: SelectChangeEvent) => {
                                    handleChange('report_scope', e.target.value);
                                    handleChange('report_day', e.target.value === SCOPE_OPTIONS.WEEKLY ? 0 : 1);
                                }}
                            >
                                {Object.keys(SCOPE_OPTIONS).map((option) => (
                                    <MenuItem key={option} value={option} id={option}>
                                        {t(`content.reports.form.scope.${option.toLocaleLowerCase()}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Tooltip title={t('content.reports.schedule')}>
                            <Box>
                                <IconButton
                                    id="btn-schedule"
                                    data-testid="btn-schedule"
                                    disabled={!hasResults}
                                    onClick={() => setIsScheduleOpen(true)}
                                >
                                    <AlarmAddIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                        <Tooltip title={t('content.reports.download')}>
                            <Box>
                                <IconButton
                                    id="btn-download"
                                    disabled={!hasResults}
                                    onClick={() => {
                                        if (apiRef.current) {
                                            const getAllRows = ({ apiRef }: GridCsvGetRowsToExportParams) =>
                                                gridExpandedSortedRowIdsSelector(apiRef);
                                            apiRef.current.exportDataAsCsv({
                                                getRowsToExport: getAllRows,
                                                utf8WithBom: true,
                                            });
                                        }
                                    }}
                                >
                                    <DownloadIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                        <Tooltip title={t('content.reports.send')}>
                            <Box>
                                <IconButton
                                    id="btn-send"
                                    data-testid="btn-send"
                                    disabled={!hasResults}
                                    onClick={() => setIsSendOpen(true)}
                                >
                                    <ForwardToInboxIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyItems: 'space-between',
                            alignItems: 'flex-end',
                            width: '100%',
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', flexGrow: 1 }}>
                            <FormControl fullWidth sx={{ width: '200px', flexShrink: 0, mr: 1, mt: 1.5 }}>
                                <InputLabel id="filter-vehicles-dropdown-label">
                                    {t('content.reports.form.filter.filters_header')}
                                </InputLabel>
                                <Select
                                    id="filter-vehicles-dropdown"
                                    data-testid="filter-vehicles-dropdown"
                                    labelId="filter-vehicles-dropdown-label"
                                    fullWidth
                                    size="small"
                                    label={t('content.reports.form.filter.filters_header')}
                                    value={deviceFilterKey}
                                    onChange={(e: SelectChangeEvent) => {
                                        const newFilter = { ...details.filters };
                                        const sessionStatuses = newFilter[SESSION_STATUS_FILTER_KEY];
                                        for (const key of Object.keys(DEVICE_FILTER_OPTIONS)) {
                                            delete newFilter[key];
                                        }
                                        if (sessionStatuses) {
                                            newFilter[SESSION_STATUS_FILTER_KEY] = sessionStatuses;
                                        }
                                        newFilter[e.target.value] = [];
                                        handleChange('filters', newFilter);
                                    }}
                                >
                                    {Object.keys(DEVICE_FILTER_OPTIONS)
                                        .filter((option) => FILTER_AVAILABLE[option].includes(details.report_type))
                                        .map((option) => {
                                            return (
                                                <MenuItem
                                                    key={option}
                                                    value={option}
                                                    id={option}
                                                    disabled={!deviceFilterOptions[option]?.length}
                                                >
                                                    {t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                                                </MenuItem>
                                            );
                                        })}
                                </Select>
                            </FormControl>
                            {deviceFilterKey == DEVICE_FILTER_OPTIONS.ALL ? null : (
                                <Autocomplete
                                    id="filter-vehicles-values-autocomplete"
                                    data-testid="filter-vehicles-values-autocomplete"
                                    multiple
                                    disableCloseOnSelect
                                    size="small"
                                    options={deviceFilterOptions[deviceFilterKey] || []}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props} style={{ padding: 0 }}>
                                            <Checkbox
                                                id={`${option}-checkbox`}
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                checked={selected}
                                                size="small"
                                            />
                                            <Typography sx={{ pt: 0.5 }}>{option}</Typography>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t(
                                                `content.reports.form.filter.${deviceFilterKey.toLocaleLowerCase()}`,
                                            )}
                                        />
                                    )}
                                    renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                                        return (
                                            <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                                                <Chip
                                                    size="small"
                                                    label={<Typography fontSize={12}>{value[0]}</Typography>}
                                                    {...getTagProps({ index: 0 })}
                                                    sx={{ borderRadius: 1 }}
                                                    onDelete={undefined}
                                                />
                                                {value.length > 1 ? (
                                                    <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                                                ) : null}
                                            </Box>
                                        );
                                    }}
                                    value={details.filters[deviceFilterKey] as string[]}
                                    onChange={(_event, value) => {
                                        const newFilter = { ...details.filters };
                                        delete newFilter[DEVICE_FILTER_OPTIONS.LICENSE_PLATE];
                                        delete newFilter[DEVICE_FILTER_OPTIONS.DEPARTMENT];
                                        newFilter[deviceFilterKey] = value;
                                        handleChange('filters', newFilter);
                                    }}
                                    sx={{
                                        minWidth: '200px',
                                        flexShrink: 0,
                                        mr: 1,
                                        mt: 1.5,
                                        '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                                        '& .MuiInputBase-input': {
                                            minWidth: '0px !important',
                                            paddingLeft: !!details.filters[deviceFilterKey]?.length
                                                ? '0px !important'
                                                : '6px',
                                            paddingRight: !!details.filters[deviceFilterKey]?.length
                                                ? '0px !important'
                                                : '4px',
                                        },
                                    }}
                                />
                            )}
                            {details.report_type == REPORT_TYPE_OPTIONS.SAFETY_EVENTS && (
                                <SafetyEventForm handleChange={handleChange} details={details} />
                            )}
                            {details.report_type == REPORT_TYPE_OPTIONS.GEOFENCE_EVENTS && (
                                <GeofenceEventForm
                                    geofences={props.geofences}
                                    handleChange={handleChange}
                                    details={details}
                                />
                            )}
                            {details.report_type == REPORT_TYPE_OPTIONS.DEVICE_WELLNESS && (
                                <DeviceWellnessForm handleChange={handleChange} details={details} />
                            )}
                            {details.report_type == REPORT_TYPE_OPTIONS.ACCIDENTS_REPORT && (
                                <AccidentReportForm handleChange={handleChange} details={details} />
                            )}
                            {details.report_type == REPORT_TYPE_OPTIONS.MALFUNCTIONS && (
                                <MalfunctionsReportForm handleChange={handleChange} details={details} />
                            )}
                            {details.report_type == REPORT_TYPE_OPTIONS.COACHING_SESSIONS && (
                                <CoachingSessionsStatusForm handleChange={handleChange} details={details} />
                            )}
                        </Box>
                        <Button
                            id="btn-generate"
                            data-testid="btn-generate"
                            variant="contained"
                            color="secondary"
                            startIcon={<PlayArrowIcon />}
                            sx={{ flexShrink: 0 }}
                            disabled={(!detailsChanged || isLoading) && !isError}
                            onClick={() => handleGenerate(details)}
                        >
                            {t('content.reports.generate')}
                        </Button>
                    </Box>
                    <Box sx={{ width: '100%', height: '100%', pt: 1, pb: 2, minHeight: 0 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                            <StyledDataGrid
                                apiRef={apiRef}
                                id="report-results-data-grid"
                                data-testid="report-results-data-grid"
                                loading={isLoading}
                                columns={columns}
                                columnBuffer={columns.length}
                                rows={rows}
                                disableRowSelectionOnClick
                                hideFooter
                                disableVirtualization={isMobile || process.env.JEST_WORKER_ID !== undefined}
                                localeText={{
                                    ...gridLocalization[i18n.languages[0]],
                                    filterPanelRemoveAll: t('table.remove_all'),
                                    columnMenuManageColumns: t('table.manage_columns'),
                                    unpin: t('table.unpin'),
                                }}
                                slots={{
                                    noRowsOverlay: () => (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {isError ? (
                                                <Typography variant="overline" color="error">
                                                    {t('content.reports.table.error_generating_report')}
                                                </Typography>
                                            ) : (
                                                <Typography variant="overline">
                                                    {t('content.reports.table.no_rows')}
                                                </Typography>
                                            )}
                                        </Box>
                                    ),
                                }}
                                onRowClick={(params) => {
                                    if (
                                        [
                                            REPORT_TYPE_OPTIONS.SAFETY_EVENTS,
                                            REPORT_TYPE_OPTIONS.GEOFENCE_EVENTS,
                                        ].includes(reportData?.report_type || '')
                                    ) {
                                        setIsLoading(true);
                                        setSelectedEventId(params.row.event_id.toString());
                                    } else if (reportData?.report_type === REPORT_TYPE_OPTIONS.ACCIDENTS_REPORT) {
                                        setIsLoading(true);
                                        setSelectedAccidentId(params.row.accident_number.toString());
                                    } else if (reportData?.report_type === REPORT_TYPE_OPTIONS.MALFUNCTIONS) {
                                        history.push(`/tasks/${params.row.malfunction_id}`);
                                    } else if (
                                        COACHING_SESSION_GRID_REPORT_TYPES.includes(reportData?.report_type || '')
                                    ) {
                                        history.push(`/coaching/${params.row.session_id}`);
                                    }
                                }}
                                getRowClassName={(params) =>
                                    'event_id' in params.row ? `${params.row.event_id}-row` : ''
                                }
                                columnHeaderHeight={42}
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    '& .MuiDataGrid-main': {
                                        borderRadius: '4px',
                                        boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.25)',
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        borderBottom: 'none',
                                        color: palette.neutral[100],
                                        '& .MuiSvgIcon-root': {
                                            fill: palette.neutral[100],
                                        },
                                    },
                                    '& .MuiDataGrid-virtualScrollerContent': {
                                        borderBottom: `1px solid ${palette.neutral[200]}`,
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        cursor: [
                                            REPORT_TYPE_OPTIONS.SAFETY_EVENTS,
                                            REPORT_TYPE_OPTIONS.GEOFENCE_EVENTS,
                                            REPORT_TYPE_OPTIONS.ACCIDENTS_REPORT,
                                            REPORT_TYPE_OPTIONS.MALFUNCTIONS,
                                            ...COACHING_SESSION_GRID_REPORT_TYPES,
                                        ].includes(reportData?.report_type || '')
                                            ? 'pointer'
                                            : 'default',
                                    },
                                    '& .MuiDataGrid-cell--editing': {
                                        padding: '0 !important',
                                    },
                                    '& .MuiDataGrid-cell:focus-within': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-row--dynamicHeight': { minHeight: '41px !important' },
                                    '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '6px' },
                                    backgroundColor: palette.neutral[50],
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                </TabPanel>
                <TabPanel value={tab} index={1} sx={{ width: '100%', height: '100%', minHeight: 0 }}>
                    <ScheduledReportsComponent
                        type="REPORTS"
                        reports={props.scheduledReports}
                        devices={props.devices}
                        drivers={props.drivers}
                        geofences={props.geofences}
                        users={props.users}
                        isLoading={props.isLoadingScheduled}
                        onGenerate={handleGenerateScheduled}
                        setTab={setTab}
                        reportType={props.reportType}
                    />
                </TabPanel>
                <TabPanel value={tab} index={2} sx={{ width: '100%', height: '100%', minHeight: 0 }}>
                    <PastReports
                        reportInstances={pastReports}
                        geofences={props.geofences}
                        users={props.users}
                        isLoading={isLoadingPastReports}
                        onRowClick={() => {
                            setReportData(undefined);
                            setIsLoading(true);
                            setTab(0);
                        }}
                    />
                </TabPanel>
            </Box>
        </Box>
    );
};

export default ReportContent;
